import { useMemo } from "react";

import { createSlice } from "@reduxjs/toolkit";
import { keys } from "lodash";

import { useAppDispatch } from "../hooks";

import {
  IStaffCategoryState,
  staffCategoryActions,
  staffCategoryInitialState,
  useSynchStaffCategories,
} from "./staffJobHierarchy/staffCategory/store";
import {
  IStaffJobState,
  staffJobActions,
  staffJobInitialState,
  useSynchStaffJobs,
} from "./staffJobHierarchy/staffJob/store";
import {
  IStaffTypeState,
  staffTypeActions,
  staffTypeInitialState,
  useSynchStaffTypes,
} from "./staffJobHierarchy/staffType/store";
import {
  facilityActions,
  facilityInitialState,
  IFacilityState,
  useSynchFacilities,
} from "./unitHierarchy/facility/store";
import {
  healthSystemActions,
  healthSystemInitialState,
  IHealthSystemState,
  useSynchHealthSystems,
} from "./unitHierarchy/healthSystem/store";
import {
  IUnitState,
  unitActions,
  unitInitialState,
  useSynchUnits,
} from "./unitHierarchy/unit/store";

// Don't forget to export setters here
//  and to consume them in bootstrap/initializers/appConfigSync.tsx
export {
  useSynchStaffCategories,
  useSynchStaffTypes,
  useSynchHealthSystems,
  useSynchFacilities,
  useSynchUnits,
  useSynchStaffJobs,
};

export type TAppConfigState = {
  staffCategory: IStaffCategoryState;
  staffType: IStaffTypeState;
  staffJob: IStaffJobState;
  healthSystem: IHealthSystemState;
  facility: IFacilityState;
  unit: IUnitState;
};

const initialState: TAppConfigState = {
  staffCategory: staffCategoryInitialState,
  staffType: staffTypeInitialState,
  staffJob: staffJobInitialState,
  healthSystem: healthSystemInitialState,
  facility: facilityInitialState,
  unit: unitInitialState,
};

export const appConfigReducer = createSlice({
  name: "appConfig",
  initialState,
  reducers: {
    resetState: () => initialState,
    ...staffCategoryActions,
    ...staffTypeActions,
    ...staffJobActions,
    ...healthSystemActions,
    ...facilityActions,
    ...unitActions,
  },
});

// All available actions of the appConfig store/reducer.
type TActions = typeof appConfigReducer.actions;
// Type that associate parameters type to each action.
type TWrappedFunctions = {
  [key in keyof TActions]: (...args: Parameters<TActions[key]>) => void;
};
/**
 * Dispatch wrapper to attach all available action to the dispatch function
 *  of the appConfig store/reducer.
 */
export const useAppConfigDispatch = () => {
  const dispatch = useAppDispatch();

  return useMemo(
    () =>
      // For each action, create a wrapper function that dispatch() the action with the same parameters.
      keys(appConfigReducer.actions).reduce(
        <K extends keyof TActions>(acc: TWrappedFunctions, fnName: K) => {
          // If action is available
          const fn = appConfigReducer.actions[fnName];
          if (fn)
            // Just dispatch the action with the same parameters.
            acc[fnName] = (...args: unknown[]) => {
              // @ts-ignore can pass its own args
              dispatch(fn(...args));
            };

          return acc;
        },
        {} as TWrappedFunctions,
      ),
    [dispatch],
  );
};

export default appConfigReducer.reducer;
